/**
 * Per-wheel render order, so barrel masks stop eating each other's
 * wheels.
 *
 * The mask is an invisible cylinder that writes depth and no colour, so
 * scene objects behind it are culled. That is exactly what it is for —
 * it stops you seeing the world through the spokes — but depth is one
 * shared buffer, so it culls the OTHER wheel too. And it is deliberately
 * wider than its wheel (radius padding plus the whole barrel depth), so
 * the bite it takes is larger than the wheel causing it. With two wheels
 * placed, the nearer one punched a circular hole in the further one
 * (2026-08-23 LUAT).
 *
 * The fix is to give each wheel its own depth pass: order wheels far to
 * near, clear depth before each, and a mask can then only reach its own
 * wheel. The nearest wheel still paints last, so the pair still looks
 * correctly stacked.
 *
 * This module is the arithmetic only — pure, so the rule is testable
 * without a GL context.
 */

/** Render-order slots reserved per wheel. Room for mask, body and the
 *  selection ring without ever colliding with the next wheel's block. */
export const WHEEL_RENDER_ORDER_STRIDE = 10

/** The placement gauge sits above every wheel, unconditionally. It is
 *  the affordance the user is aiming with, so it is never allowed to
 *  fall behind geometry — and it must out-rank the selection ring,
 *  which previously shared 999 and could win a tie (2026-08-23). */
export const GAUGE_RENDER_ORDER = 10_000

/** Selection rings: above their own wheel, below the gauge. */
export const SELECTION_RING_RENDER_ORDER = 500

/** ?depthPrepass — a TRUE pre-pass: every wheel's depth is laid down
 *  before anything is shaded.
 *
 *  It ran at the tail of the opaque queue first, which fixed the
 *  gradient's occlusion but left the two stacking by different rules —
 *  bodies by painter order (whole-object, flipping at the object-centre
 *  crossover) and the gradient by depth (per-pixel, at the true
 *  geometric one). They popped at different moments and read as
 *  separate objects (2026-08-23 LUAT). Running first lets BOTH resolve
 *  against the same depth, so there is no flip to be out of step. */
export const DEPTH_RESTAMP_RENDER_ORDER = -1000

export interface WheelDistance {
  readonly id: string
  readonly distance: number
}

/**
 * Furthest wheel first, so the nearest paints last and ends up on top.
 *
 * Stable: equal distances keep their existing order rather than
 * swapping every frame, which would strobe when two wheels sit the same
 * distance away.
 */
export function sortWheelsFarToNear<T extends WheelDistance>(wheels: readonly T[]): T[] {
  return wheels
    .map((wheel, index) => ({ wheel, index }))
    .sort((a, b) => b.wheel.distance - a.wheel.distance || a.index - b.index)
    .map(({ wheel }) => wheel)
}

/**
 * The render-order slots for the wheel at `position` in the far-to-near
 * ordering. Each wheel owns a block, so its parts can never interleave
 * with another wheel's.
 */
export function renderOrderForWheel(position: number): {
  mask: number
  body: number
  gradient: number
  overlay: number
} {
  const base = position * WHEEL_RENDER_ORDER_STRIDE
  return {
    // Mask first: it stamps depth for the body that follows.
    mask: base,
    body: base + 1,
    // The gradient rides in the SAME queue as the body (see the
    // occluder), so it lands inside this wheel's pass and travels with
    // it — one object, one set of occlusion rules.
    gradient: base + 2,
    // Selection ring above its own wheel, below the next wheel's mask.
    overlay: base + 3,
  }
}
